import { useState, useEffect } from "react";
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Modal,
  message,
  Table,
  Typography,
  Card,
  Alert,
  Statistic,
  Space,
  Select,
  DatePicker
} from "antd";
import { 
  PlusOutlined, 
  MinusOutlined, 
  EditOutlined, 
  DeleteOutlined,
  FilePdfOutlined,
  FileExcelOutlined 
} from "@ant-design/icons";
import axios from "axios";
import moment from "moment";

const { Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const LOW_STOCK_THRESHOLD = 10;
const OVERSTOCK_THRESHOLD = 100;

const StockSummaryCard = ({ title, value, status }) => {
  const statusColors = {
    low: "#ff4d4f",
    over: "#faad14",
    normal: "#52c41a",
  };

  return (
    <Card>
      <Statistic
        title={title}
        value={value}
        valueStyle={{
          color: statusColors[status],
          fontSize: "1.8em",
          fontWeight: "bold",
        }}
      />
    </Card>
  );
};

const StoreIssueVoucher = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [stock, setStock] = useState({});
  const [rawMaterialData, setRawMaterialData] = useState([]);
  const [editingVoucher, setEditingVoucher] = useState(null);
  const [itemsList, setItemsList] = useState([]);
  const [token] = useState({ borderRadiusLG: 8 });
  
  // Report state
  const [reportParams, setReportParams] = useState({
    type: 'monthly',
    year: moment().year(),
    month: moment().month() + 1,
    quarter: 1,
    customRange: null,
  });
  const [reportData, setReportData] = useState([]);

  const reportColumns = [
    { title: 'SIV Number', dataIndex: 'siv_number', key: 'siv_number' },
    { title: 'Item Code', dataIndex: 'item_code', key: 'item_code' },
    { title: 'Item Name', dataIndex: 'item_name', key: 'item_name' },
    { title: 'Quantity', dataIndex: 'quantity', key: 'quantity' },
    { title: 'Unit', dataIndex: 'unit', key: 'unit' },
    { title: 'Department', dataIndex: 'department', key: 'department' },
    { title: 'Purpose', dataIndex: 'purpose', key: 'purpose' },
    { title: 'DRN Date', dataIndex: 'drn_date', key: 'drn_date' },
  ];

  useEffect(() => {
    const itemsMap = new Map();
    rawMaterialData.forEach(note => {
      note.items.forEach(item => {
        if (!itemsMap.has(item.item_code)) {
          itemsMap.set(item.item_code, {
            code: item.item_code,
            name: item.item_name,
            unit: item.unit_of_measurement || 'N/A'
          });
        }
      });
    });
    setItemsList(Array.from(itemsMap.values()));
  }, [rawMaterialData]);

  const calculateStockMetrics = () => {
    const stockEntries = Object.entries(stock);
    return {
      totalItems: stockEntries.length,
      totalStock: stockEntries.reduce((sum, [_, quantity]) => sum + quantity, 0),
      lowStockItems: stockEntries.filter(([_, quantity]) => quantity <= LOW_STOCK_THRESHOLD).length,
    };
  };

  const { totalItems, totalStock, lowStockItems } = calculateStockMetrics();

  const fetchData = async () => {
    try {
      const [vouchersResponse, materialsResponse] = await Promise.all([
        axios.get("https://agpipefitting.org.et/api/store-issue-voucher"),
        axios.get("https://agpipefitting.org.et/api/receiving-notes"),
      ]);

      setVouchers(vouchersResponse.data);
      setRawMaterialData(materialsResponse.data);
      
      const groupedData = vouchersResponse.data.map(voucher => ({
        ...voucher,
        key: voucher.siv_number,
        items: voucher.items.map(item => ({
          ...item,
          key: `${voucher.siv_number}-${item.item_code}`
        }))
      }));
      setDataSource(groupedData);

      const stockCalc = {};
      materialsResponse.data.forEach(note => {
        note.items.forEach(item => {
          stockCalc[item.item_code] = (stockCalc[item.item_code] || 0) + Number(item.quantity);
        });
      });

      vouchersResponse.data.forEach(voucher => {
        voucher.items.forEach(item => {
          stockCalc[item.item_code] = (stockCalc[item.item_code] || 0) - Number(item.quantity);
        });
      });

      setStock(stockCalc);
    } catch (error) {
      message.error("Failed to load data");
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenModal = () => {
    form.resetFields();
    setModalVisible(true);
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setEditingVoucher(null);
  };

  const handleEdit = (record) => {
    const voucher = vouchers.find(v => v.siv_number === record.siv_number);
    const itemsWithNames = voucher.items.map(item => ({
      ...item,
      item_name: itemsList.find(i => i.code === item.item_code)?.name || 'N/A'
    }));
    
    form.setFieldsValue({
      ...voucher,
      drn_date: moment(voucher.drn_date),
      items: itemsWithNames.map(item => ({
        ...item,
        quantity: item.quantity.toString(),
      })),
    });
    setEditingVoucher(voucher);
    setModalVisible(true);
  };

  const handleDelete = async (record) => {
    Modal.confirm({
      title: "Confirm Delete",
      content: "Are you sure you want to delete this voucher?",
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        try {
          await axios.delete(`https://agpipefitting.org.et/api/store-issue-voucher/${record.siv_number}`);
          message.success("Voucher deleted successfully");
          await fetchData();
        } catch (error) {
          message.error("Failed to delete voucher");
        }
      },
    });
  };

  const onFinish = async (values) => {
    try {
      setLoading(true);
      const processedValues = {
        ...values,
        drn_date: values.drn_date.format('YYYY-MM-DD'),
        items: values.items.map(item => ({
          ...item,
          quantity: Number(item.quantity),
        })),
      };

      const endpoint = editingVoucher 
        ? `https://agpipefitting.org.et/api/store-issue-voucher/${editingVoucher.siv_number}`
        : "https://agpipefitting.org.et/api/store-issue-voucher";

      const method = editingVoucher ? 'put' : 'post';
      
      await axios[method](endpoint, processedValues);
      
      await fetchData();
      message.success(`Voucher ${editingVoucher ? "updated" : "created"} successfully`);
      handleCloseModal();
    } catch (error) {
      message.error(error.response?.data?.message || "Operation failed");
    } finally {
      setLoading(false);
    }
  };

  const handleItemSelect = (selectedName, option, fieldName) => {

    const code = option.code; // Assuming the code is passed as a prop to Option
    
    form.setFieldsValue({
    
    items: {
    
    [fieldName]: {
    
    item_code: code
    
    }
    
    }
    
    });
    
    };

  const handleGenerateReport = () => {
    const filteredVouchers = vouchers.filter(voucher => {
      const voucherDate = moment(voucher.drn_date);
      switch(reportParams.type) {
        case 'monthly':
          return voucherDate.year() === reportParams.year && 
                 voucherDate.month() + 1 === reportParams.month;
        case 'quarterly':
          const quarterStartMonth = (reportParams.quarter - 1) * 3;
          const quarterEndMonth = quarterStartMonth + 2;
          return voucherDate.year() === reportParams.year && 
                voucherDate.month() >= quarterStartMonth && 
                voucherDate.month() <= quarterEndMonth;
        case 'yearly':
          return voucherDate.year() === reportParams.year;
        case 'custom':
          if (!reportParams.customRange || reportParams.customRange.length !== 2) return false;
          const start = reportParams.customRange[0];
          const end = reportParams.customRange[1];
          return voucherDate.isBetween(start, end, null, '[]');
        default:
          return false;
      }
    });

    const items = filteredVouchers.flatMap(voucher => 
      voucher.items.map(item => ({
        ...item,
        siv_number: voucher.siv_number,
        drn_date: voucher.drn_date,
        department: voucher.department,
        purpose: voucher.purpose,
      }))
    );

    setReportData(items);
  };

  const handleExportCSV = () => {
    if (reportData.length === 0) return;

    const csvHeaders = [
      'SIV Number',
      'Item Code',
      'Item Name',
      'Quantity',
      'Unit',
      'Department',
      'Purpose',
      'DRN Date',
    ].join(',');

    const csvRows = reportData.map(item => 
      [
        `"${item.siv_number}"`,
        `"${item.item_code}"`,
        `"${item.item_name}"`,
        item.quantity,
        `"${item.unit}"`,
        `"${item.department}"`,
        `"${item.purpose}"`,
        `"${item.drn_date}"`,
      ].join(',')
    ).join('\n');

    const csvContent = `${csvHeaders}\n${csvRows}`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'report.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const columns = [
    { title: "SIV Number", dataIndex: "siv_number", key: "siv_number" },
    { title: "Requisition Number", dataIndex: "requisition_number", key: "requisition_number" },
    { title: "Department", dataIndex: "department", key: "department" },
    { title: "Purpose", dataIndex: "purpose", key: "purpose" },
    { title: "DRN Date", dataIndex: "drn_date", key: "drn_date" },
    {
      title: "Total Items",
      key: "total_items",
      render: (_, record) => record.items.length
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Button danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)} />
        </Space>
      )
    }
  ];

  const expandedRowRender = (record) => {
    const itemColumns = [
      { title: "Item Code", dataIndex: "item_code", key: "item_code" },
      { title: "Item Name", dataIndex: "item_name", key: "item_name" },
      {
        title: "Quantity",
        dataIndex: "quantity",
        key: "quantity",
        render: (value) => Number(value).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })
      },
      { title: "Unit", dataIndex: "unit", key: "unit" },
      {
        title: "Available Stock",
        dataIndex: "item_code",
        key: "available_stock",
        render: (code) => {
          const currentStock = stock[code] || 0;
          const isLow = currentStock <= LOW_STOCK_THRESHOLD;
          return (
            <span style={{ color: isLow ? "#ff4d4f" : "#52c41a" }}>
              {Number(currentStock).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })} {isLow && "(Low Stock)"}
            </span>
          );
        }
      }
    ];
  
    return (
      <Table
        columns={itemColumns}
        dataSource={record.items}
        pagination={false}
        bordered
        rowKey="key"
      />
    );
  };
  

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: "0 auto" }}>
      <Title level={3} style={{ textAlign: "center", marginBottom: 24 }}>
        Issued Voucher Raw Material Management
      </Title>

      <Row gutter={16} style={{ marginBottom: 24 }}>
  <Col span={6}>
    <StockSummaryCard
      title="Total Items"
      value={totalItems.toLocaleString()} // Format the value as a number with commas
      status={totalItems === 0 ? "low" : "normal"}
    />
  </Col>
  <Col span={6}>
    <StockSummaryCard
      title="Total Stock"
      value={Number(totalStock).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} // Format as decimal with 2 places
      status={totalStock === 0 ? "low" : "normal"}
    />
  </Col>
  <Col span={6}>
    <StockSummaryCard
      title="Low Stock Items"
      value={lowStockItems.toLocaleString()} // Format the value as a number with commas
      status={lowStockItems > 0 ? "low" : "normal"}
    />
  </Col>
</Row>


      {lowStockItems > 0 && (
        <Alert
          message={`Low Stock Alert: ${lowStockItems} items below minimum threshold (${LOW_STOCK_THRESHOLD})`}
          type="warning"
          showIcon
          closable
          style={{ marginBottom: 16 }}
        />
      )}

      <Card className="mb-4 shadow-sm" title="Generate Reports">
        <Row gutter={16} align="middle">
          <Col span={6}>
            <Select
              value={reportParams.type}
              onChange={type => setReportParams({ ...reportParams, type })}
              style={{ width: '100%' }}
            >
              <Option value="monthly">Monthly</Option>
              <Option value="quarterly">Quarterly</Option>
              <Option value="yearly">Yearly</Option>
              <Option value="custom">Custom Range</Option>
            </Select>
          </Col>

          {reportParams.type === 'yearly' && (
            <Col span={4}>
              <DatePicker
                picker="year"
                value={moment().year(reportParams.year)}
                onChange={date => setReportParams({ ...reportParams, year: date.year() })}
              />
            </Col>
          )}

          {reportParams.type === 'quarterly' && (
            <>
              <Col span={4}>
                <DatePicker
                  picker="year"
                  value={moment().year(reportParams.year)}
                  onChange={date => setReportParams({ ...reportParams, year: date.year() })}
                />
              </Col>
              <Col span={4}>
                <Select
                  value={reportParams.quarter}
                  onChange={quarter => setReportParams({ ...reportParams, quarter })}
                  style={{ width: '100%' }}
                >
                  <Option value={1}>Q1</Option>
                  <Option value={2}>Q2</Option>
                  <Option value={3}>Q3</Option>
                  <Option value={4}>Q4</Option>
                </Select>
              </Col>
            </>
          )}

          {reportParams.type === 'monthly' && (
            <Col span={6}>
              <DatePicker
                picker="month"
                value={moment().year(reportParams.year).month(reportParams.month - 1)}
                onChange={date => setReportParams({ 
                  ...reportParams, 
                  year: date.year(),
                  month: date.month() + 1
                })}
              />
            </Col>
          )}

          {reportParams.type === 'custom' && (
            <Col span={8}>
              <RangePicker
                value={reportParams.customRange}
                onChange={dates => setReportParams({ ...reportParams, customRange: dates })}
              />
            </Col>
          )}

          <Col>
            <Button 
              type="primary" 
              onClick={handleGenerateReport}
              icon={<FilePdfOutlined />}
            >
              Generate Report
            </Button>
          </Col>

          {reportData.length > 0 && (
            <Col>
              <Button 
                type="default" 
                onClick={handleExportCSV}
                icon={<FileExcelOutlined />}
              >
                Export CSV
              </Button>
            </Col>
          )}
        </Row>

        {reportData.length > 0 && (
          <div className="mt-4">
            <Space>
              <Statistic
                title="Total Entries"
                value={reportData.length}
                className="me-4"
              />
              <Statistic
                title="Total Value"
                value={reportData.reduce((sum, entry) => sum + 
                  (entry.quantity * (entry.unit_price || 0)), 0)
                  .toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
              />
            </Space>

            <Table
              columns={reportColumns}
              dataSource={reportData}
              rowKey="key"
              className="mt-4"
              pagination={{ pageSize: 10 }}
              scroll={{ x: true }}
            />
          </div>
        )}
      </Card>

      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={handleOpenModal}
        style={{ marginBottom: 16, borderRadius: token.borderRadiusLG }}
        size="large"
      >
        Create New Voucher
      </Button>

      <Table
        bordered
        dataSource={dataSource}
        columns={columns}
        rowKey="key"
        pagination={{ pageSize: 5 }}
        scroll={{ x: true }}
        expandable={{
          expandedRowRender,
          rowExpandable: (record) => record.items.length > 0,
        }}
      />

      <Modal
        title={editingVoucher ? "Edit Voucher" : "Create Voucher"}
        open={modalVisible}
        onCancel={handleCloseModal}
        footer={null}
        width={800}
        destroyOnClose
      >
        <Form form={form} onFinish={onFinish} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="SIV Number" name="siv_number" rules={[{ required: true }]}>
                <Input placeholder="SIV-001" disabled={!!editingVoucher} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Requisition Number" name="requisition_number" rules={[{ required: true }]}>
                <Input placeholder="REQ-2023-001" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Department" name="department" rules={[{ required: true }]}>
                <Input placeholder="Production Department" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Purpose" name="purpose" rules={[{ required: true }]}>
                <Input placeholder="Material Issue" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="DRN Date" name="drn_date" rules={[{ required: true }]}>
                <DatePicker 
                  format="YYYY-MM-DD"
                  style={{ width: '100%' }}
                  placeholder="Select Date"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.List name="items">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Row key={key} gutter={16} align="middle" style={{ marginBottom: 8 }}>
                     <Col span={8}>
    <Form.Item
      {...restField}
      name={[name, "item_name"]}
      rules={[{ required: true }]}
    >
      <Select
        showSearch
        placeholder="Select Item"
        optionFilterProp="children"
        filterOption={(input, option) =>
          option.children.toLowerCase().includes(input.toLowerCase())
        }
        onChange={(value, option) => {
          // Set item code when item is selected
          form.setFieldsValue({
            items: {
              [name]: {
                item_code: option.code
              }
            }
          });
        }}
      >
        {itemsList.map(item => (
          <Option 
            key={item.code} 
            value={item.name}
            code={item.code} // Pass item code as a prop
          >
            {item.name}
          </Option>
        ))}
      </Select>
    </Form.Item>
  </Col>
  <Col span={4}>
  <Form.Item
    {...restField}
    name={[name, "quantity"]}
    rules={[{ 
      required: true, 
      message: "Required",
      validator: (_, value) => {
        // Fetch item code and available stock
        const code = form.getFieldValue(['items', name, 'item_code']);
        const available = stock[code] || 0;

        // Ensure quantity is a decimal value (2 decimal places)
        const decimalValue = parseFloat(value).toFixed(2);

        // Check if quantity exceeds available stock
        if (decimalValue > available) {
          return Promise.reject(`Exceeds available stock (${available})`);
        }
        return Promise.resolve();
      }
    }]}
  >
    <Input 
      type="number" 
      placeholder="Qty" 
      min={0.01} // Ensuring that the value is a decimal
      step="0.01" // Allow decimals in input
      size="large"
    />
  </Form.Item>
</Col>

                    <Col span={4}>
                      <Form.Item
                        {...restField}
                        name={[name, "unit"]}
                        rules={[{ required: true }]}
                      >
                        <Input placeholder="Unit" />
                      </Form.Item>
                    </Col>
                    <Col span={2}>
                      <Button
                        danger
                        icon={<MinusOutlined />}
                        onClick={() => remove(name)}
                        disabled={fields.length === 1}
                      />
                    </Col>
                  </Row>
                ))}
                <Form.Item>
                  <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                    Add Item
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>

          <Form.Item style={{ marginTop: 24, textAlign: "right" }}>
            <Button onClick={handleCloseModal} style={{ marginRight: 8 }}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              {editingVoucher ? "Update Voucher" : "Create Voucher"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default StoreIssueVoucher;